"use client";

// CreateYours — the ONE door from the website into the experience.
//
// Every "create yours" / "save it" on a public surface fires
// `innernet:create-yours`; this component, mounted once in the root layout,
// answers it with THE door: a blue card with netti opening out of its bottom
// edge, a heading, one line under it, and two ways in. "sign up" starts the
// birth right here, over the page you are on: the room flushes to black from
// the bottom and the cell appears, with nothing navigated away. "log in" is the
// other door.
//
// ONE CARD, EVERY REASON. The card never changes: same ground, same creature,
// same two ways in, whether you met it on the homepage or on a skill in the
// commons. Only the HEADING and the line under it change, because the reason
// you pressed it is different — from the commons you were not looking for an
// account, you were looking at a skill you want to keep. Add a reason by adding
// a row to COPY, never by building a second card.
//
// THE SKIN IS A THEME, not a hard-coded card. `DOOR_THEME` carries the ground
// and the creature on it, published as custom properties the stylesheet reads,
// so a later surface can wear a different colour or a different netti without
// touching this layout. Today there is one theme and everything wears it.
//
// It is THE sheet (law_sheet_rises): it rises from the bottom edge, which is
// exactly where netti opens out of, the heading and the line under it centred in
// its body, "log in" quiet at the foot's left and "sign up" the one action in
// the corner. The sheet wears the door's ground and light ink (onboarding-birth.css,
// .cy-*); everything else about it, motion and focus and Esc, is the sheet's.
//
// Mounted once, renders nothing until asked, and never on a signed-in surface
// (the CTAs that fire it are hidden there).

import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import Sheet from "../os/Sheet";
import { PillButton } from "../ui";
import DoorMark from "../brand/DoorMark";
import OnboardingBirth from "./OnboardingBirth";

export const CREATE_YOURS_EVENT = "innernet:create-yours";

/** why the door was opened — the modal answers that reason. */
export type CreateYoursIntent = "save-skill";

/** open the door from anywhere — a CTA, a link, netti's chat. */
export function openCreateYours(intent?: CreateYoursIntent) {
  if (typeof window === "undefined") return;
  // ⚠️ passed straight to onClick somewhere, this arrives as a MouseEvent. Only
  // a real intent may travel; anything else opens the ordinary door.
  const wanted = typeof intent === "string" ? intent : undefined;
  window.dispatchEvent(new CustomEvent(CREATE_YOURS_EVENT, { detail: { intent: wanted } }));
}

/** the door's skin: the card's ground, and the creature standing in it. */
export type DoorTheme = {
  /** the card itself */
  ground: string;
  /** netti, already flipped the way she stands in the card */
  bloom: string;
};

export const DOOR_THEME: DoorTheme = {
  ground: "#1a5cb8",
  bloom: "/brand/netti-bloom-hero.webp",
};

// ── ONE PICTURE, NOT TWO ARRIVALS ───────────────────────────────────────────
// The card and netti must land in the same frame. The card is CSS and paints
// the instant it mounts; netti is a 136 kB image that, left alone, is only
// asked for when the card opens — so the blue card sat empty for a second or
// two while she downloaded. Now the page fetches her while it is idle (this
// component is mounted in the root layout, so on every page the door can open
// from), and an open waits for her before it shows the card. The wait is capped: on a cold, slow line the door opens anyway after
// OPEN_WAIT_MS rather than feel like a dead button.
const OPEN_WAIT_MS = 1200;
const held: HTMLImageElement[] = []; // keeps the decoded image alive for the page's life
let bloomReady: Promise<void> | null = null;

/**
 * fetch netti once; every caller shares the same promise. Ready means her
 * bytes are here, so the sheet's ground (she is its background image) has them
 * the moment it mounts, and the rise hides any frame the paint is behind. The
 * decode is started too, as a warm-up, but nothing waits on it: `decode()` does
 * not settle in a hidden tab, and a door must not hang on that.
 */
function warmBloom(): Promise<void> {
  if (bloomReady) return bloomReady;
  const img = new Image();
  held.push(img);
  bloomReady = new Promise<void>((done) => {
    img.onload = img.onerror = () => done(); // a failed load must never block the door
    img.src = DOOR_THEME.bloom;
    if (img.complete) done();
  });
  bloomReady.then(() => img.decode()).catch(() => {});
  return bloomReady;
}

const COPY: Record<"default" | CreateYoursIntent, { title: string; sub: React.ReactNode; cta: string }> = {
  default: {
    title: "create your innernet.",
    sub: (
      <>
        one memory for every ai you use, kept current on its own. it starts with a cell, and a netti
        grows out of it.
      </>
    ),
    cta: "sign up",
  },
  "save-skill": {
    title: "save this to your innernet memory",
    sub: <>save it once to your innernet memory, and no matter what tool you use, it stays connected.</>,
    cta: "sign up",
  },
};

export default function CreateYours() {
  const [open, setOpen] = useState(false);
  const [born, setBorn] = useState(false);
  // "sign up" pressed: the card itself turns to night first, then the whole
  // screen fades to black (the birth) and the cell arrives out of it
  const [night, setNight] = useState(false);
  const [intent, setIntent] = useState<CreateYoursIntent | null>(null);

  // the latest open asked for; an older one still waiting on netti yields to it
  const openTicket = useRef(0);
  const actsRef = useRef<HTMLDivElement | null>(null);

  // a keyboard lands IN the door, not on the page behind it. The sheet moves
  // focus to its close button as it opens, so this waits a beat and takes it on
  // to the one action.
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => actsRef.current?.querySelector<HTMLElement>("button")?.focus({ preventScroll: true }), 80);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    // warm netti once the page has finished its own work
    // (Safari has no requestIdleCallback)
    const hasIdle = "requestIdleCallback" in window;
    const idle = hasIdle
      ? window.requestIdleCallback(() => void warmBloom(), { timeout: 4000 })
      : window.setTimeout(() => void warmBloom(), 1500);
    const on = (e: Event) => {
      const detail = (e as CustomEvent<{ intent?: CreateYoursIntent }>).detail;
      const ticket = ++openTicket.current;
      const show = () => {
        if (ticket !== openTicket.current) return;
        setIntent(detail?.intent ?? null);
        setNight(false);
        setOpen(true);
      };
      Promise.race([warmBloom(), new Promise((r) => window.setTimeout(r, OPEN_WAIT_MS))]).then(show);
    };
    window.addEventListener(CREATE_YOURS_EVENT, on);
    return () => {
      window.removeEventListener(CREATE_YOURS_EVENT, on);
      if (hasIdle) window.cancelIdleCallback(idle);
      else window.clearTimeout(idle);
    };
  }, []);

  const copy = COPY[intent && intent in COPY ? intent : "default"];

  // The hand-over from the card to the birth is two timers. They are kept so that
  // leaving the birth (Escape, "not now") can cancel them: pressed inside the first
  // half second, a leave used to be undone by the 480ms timer still pending, which
  // mounted the birth again over a page the person had just stepped back to.
  const handover = useRef<number[]>([]);
  const clearHandover = useCallback(() => {
    handover.current.forEach((t) => window.clearTimeout(t));
    handover.current = [];
  }, []);
  useEffect(() => clearHandover, [clearHandover]);

  const begin = useCallback(() => {
    setNight(true);
    clearHandover();
    // the birth mounts as an overlay over the darkened card and fades the
    // rest of the page to black; the card steps out beneath it
    handover.current.push(
      window.setTimeout(() => {
        setBorn(true);
        // night stays until the next open: the sheet sinks dark, under the birth
        handover.current.push(window.setTimeout(() => setOpen(false), 900));
      }, 480),
    );
  }, [clearHandover]);

  // "not now" / Escape at any stage of the birth: the birth goes, and so does the
  // card behind it, back to the page they were on.
  const leaveBirth = useCallback(() => {
    clearHandover();
    setBorn(false);
    setOpen(false);
  }, [clearHandover]);

  return (
    <>
      <Sheet
        open={open}
        // The card is still on its way down while the birth stands over it, and its
        // Esc handler answers first (it is a capture listener that swallows the key),
        // so an Esc there is the person leaving the birth, not just the card. In the
        // half second after "sign up", before the birth exists, it cancels the birth
        // that was about to start.
        onClose={() => (born ? leaveBirth() : (clearHandover(), setOpen(false)))}
        labelledBy="cy-title"
        size="sm"
        height="fit"
        panelClassName="cy-sheet"
        panelStyle={{ "--cy-ground": DOOR_THEME.ground, "--cy-bloom": `url(${DOOR_THEME.bloom})` } as CSSProperties}
        backdropClassName={night ? "cy-backdrop is-night" : "cy-backdrop"}
        footStart={
          <a className="cy-login" href="/login">
            already have an account? log in
          </a>
        }
        footer={
          <div ref={actsRef}>
            <PillButton
              variant="oauth"
              className="btn-pill--solid cy-pill"
              icon={<DoorMark />}
              onClick={begin}
            >
              {copy.cta}
            </PillButton>
          </div>
        }
      >
        {/* netti opens out of the sheet's bottom edge (the panel's ::before,
            onboarding-birth.css); the words sit above her */}
        <div className="cy">
          <h2 id="cy-title" className="cy-title">{copy.title}</h2>
          <p className="cy-sub">{copy.sub}</p>
        </div>
      </Sheet>
      {born && <OnboardingBirth overlay autoStart onLeave={leaveBirth} />}
    </>
  );
}
